import { useState, useEffect } from "react";
import axios from "axios";
import {
  FaCalendarAlt,
  FaPen,
  FaTrashAlt,
  FaBullhorn,
  FaPlusCircle,
  FaSave,
} from "react-icons/fa";

export default function Notice() {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [paragraph, setParagraph] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]); // Holds all fetched notices
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await axios.get(
        "https://mosque-backend-yfae.vercel.app/allNotice",
      );
      setData(response.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const formatDate = (inputDate) => {
    if (!inputDate) return "N/A";
    const dateObj = new Date(inputDate);
    if (isNaN(dateObj.getTime())) return inputDate; // If already formatted, return raw

    const options = { day: "numeric", month: "short", year: "numeric" };
    const formattedDate = dateObj.toLocaleDateString("en-GB", options);
    const day = dateObj.getDate();
    const suffix = ["th", "st", "nd", "rd"][
      day % 10 > 3 || (day % 100 >= 11 && day % 100 <= 13) ? 0 : day % 10
    ];
    return `${day}${suffix} ${formattedDate.split(" ")[1]} ${
      formattedDate.split(" ")[2]
    }`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const submissionDate = date.includes("-") ? formatDate(date) : date;

    try {
      if (editId) {
        await axios.put(
          `https://mosque-backend-yfae.vercel.app/updateNotice/${editId}`,
          { title, date: submissionDate, paragraph },
        );
      } else {
        await axios.post("https://mosque-backend-yfae.vercel.app/notice", {
          title,
          date: submissionDate,
          paragraph,
        });
      }
      fetchData();
      setTitle("");
      setDate("");
      setParagraph("");
      setEditId(null);
    } catch (error) {
      console.error("Error submitting data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setTitle(item.title);
    setDate(item.date);
    setParagraph(item.paragraph);
    setEditId(item._id);
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this notice?",
    );
    if (confirmed) {
      try {
        await axios.delete(
          `https://mosque-backend-yfae.vercel.app/deleteNotice/${id}`,
        );
        fetchData();
      } catch (error) {
        console.error("Error deleting data:", error);
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      {/* Header Dashboard Banner */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-8 border border-gray-100 text-center">
        <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight flex justify-center items-center gap-3">
          <FaBullhorn className="text-orange-500 text-2xl animate-pulse" />
          Notice Board Control Panel
        </h1>
        <p className="text-gray-500 mt-1 text-sm">
          Publish and manage announcements instantly for your active database
          ledger
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Form Column */}
        <div className="lg:col-span-4 bg-white shadow-md rounded-xl p-6 border border-gray-200 lg:sticky lg:top-6">
          <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center gap-2 border-b pb-3">
            {editId ? (
              <>
                <FaPen className="text-blue-500 text-sm" /> Update Notice
              </>
            ) : (
              <>
                <FaPlusCircle className="text-emerald-500 text-sm" /> Create
                Notice
              </>
            )}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider">
                Notice Title
              </label>
              <input
                type="text"
                placeholder="e.g., Schedule Change Alert"
                className="w-full mt-1.5 p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all font-medium text-gray-800"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider">
                Publish Date
              </label>
              <input
                type={editId && !date.includes("-") ? "text" : "date"}
                className="w-full mt-1.5 p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all font-medium text-gray-800"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                onClick={(e) => {
                  if (editId) e.target.type = "date";
                }}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider">
                Notice Paragraph Body
              </label>
              <textarea
                placeholder="Write detailed announcements information details here..."
                className="w-full mt-1.5 p-2.5 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all font-medium text-gray-800"
                rows="5"
                value={paragraph}
                onChange={(e) => setParagraph(e.target.value)}
                required
              ></textarea>
            </div>

            <button
              type="submit"
              className={`w-full text-white font-semibold text-sm py-2.5 rounded-lg shadow-sm transition flex justify-center items-center gap-2 ${
                editId
                  ? "bg-blue-600 hover:bg-blue-700 shadow-blue-100"
                  : "bg-orange-500 hover:bg-orange-600 shadow-orange-100"
              }`}
              disabled={loading}
            >
              <FaSave />
              {loading
                ? "Processing..."
                : editId
                  ? "Save Modifications"
                  : "Publish Document"}
            </button>

            {editId && (
              <button
                type="button"
                onClick={() => {
                  setEditId(null);
                  setTitle("");
                  setDate("");
                  setParagraph("");
                }}
                className="w-full text-xs text-gray-500 hover:text-gray-700 font-semibold text-center block pt-1 hover:underline"
              >
                Cancel Edit Mode
              </button>
            )}
          </form>
        </div>

        {/* Right Active Row Items Column */}
        <div className="lg:col-span-8 bg-white shadow-md rounded-xl p-6 border border-gray-200">
          <h2 className="text-xl font-bold mb-4 text-gray-800 flex items-center gap-2 border-b pb-3">
            <FaCalendarAlt className="text-orange-500 text-sm" /> Live Notices
            Record List ({data.length})
          </h2>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm border border-gray-100 rounded-lg overflow-hidden">
              <thead className="bg-gray-50 text-gray-700 uppercase text-xs font-bold tracking-wider">
                <tr>
                  <th className="p-4 text-left">Title Info</th>
                  <th className="p-4 text-left">Timeline Date</th>
                  <th className="p-4 text-left">Description Details</th>
                  <th className="p-4 text-center">Control Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {data.length > 0 ? (
                  data.map((item) => (
                    <tr
                      key={item._id}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="p-4 font-bold text-gray-900 min-w-[160px] align-top">
                        {item.title}
                      </td>
                      <td className="p-4 whitespace-nowrap text-gray-600 font-semibold font-mono text-xs align-top">
                        <span className="bg-orange-50 text-orange-700 px-2.5 py-1 rounded border border-orange-100 inline-block shadow-sm">
                          {item.date}
                        </span>
                      </td>
                      <td className="p-4 text-gray-600 leading-relaxed font-normal max-w-sm whitespace-pre-line break-words align-top">
                        {item.paragraph}
                      </td>
                      <td className="p-4 text-center align-top">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleEdit(item)}
                            className="inline-flex items-center gap-1 font-semibold text-xs text-blue-600 hover:text-blue-800 border border-blue-200 hover:border-blue-300 px-2.5 py-1.5 bg-blue-50/50 rounded-lg transition-all shadow-sm"
                          >
                            <FaPen className="text-[10px]" /> Edit
                          </button>
                          <button
                            onClick={() => handleDelete(item._id)}
                            className="inline-flex items-center gap-1 font-semibold text-xs text-red-600 hover:text-red-800 border border-red-200 hover:border-red-300 px-2.5 py-1.5 bg-red-50/50 rounded-lg transition-all shadow-sm"
                          >
                            <FaTrashAlt className="text-[10px]" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="4"
                      className="text-center py-12 text-gray-400 font-medium bg-gray-50/20 italic"
                    >
                      No active announcements found inside the system directory
                      ledger
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
